import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, HelpCircle, X, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

// module-level cache so navigating between pages doesn't refetch constantly
const _cache = {};
const TTL = 5 * 60 * 1000;

/**
 * DataTrustBanner — warns when the current view's underlying data includes
 * sample (fake) or unverified records. Rendered once in AppLayout, keyed by route.
 */
export function DataTrustBanner({ moduleKey, canManage = false }) {
  const [data, setData] = useState(() => _cache[moduleKey]?.data || null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!moduleKey) return;
    setDismissed(false);
    const hit = _cache[moduleKey];
    if (hit && Date.now() - hit.ts < TTL) { setData(hit.data); return; }
    let alive = true;
    api.get(`/provenance/module/${moduleKey}`)
      .then((r) => { if (alive) { _cache[moduleKey] = { ts: Date.now(), data: r.data }; setData(r.data); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [moduleKey]);

  if (!moduleKey || !data || dismissed) return null;
  const c = data.counts || {};
  const sample = c.sample || 0;
  const unverified = c.unverified || 0;
  // Only the rare sample/demo (fake-data) case warrants a banner. The routine
  // "unverified origin" case is intentionally silent — it was noise on every
  // page. The Data Provenance admin page still surfaces unverified counts.
  if (sample === 0) return null;

  const isSample = sample > 0;
  const Icon = isSample ? AlertTriangle : HelpCircle;
  const tone = isSample
    ? "bg-rose-50 border-rose-200 text-rose-800"
    : "bg-amber-50 border-amber-200 text-amber-900";
  const msg = isSample
    ? `This view includes ${sample.toLocaleString()} sample/demo record${sample === 1 ? "" : "s"} (not real data)${unverified ? ` and ${unverified.toLocaleString()} of unverified origin` : ""}. Don't base decisions on flagged records.`
    : `${unverified.toLocaleString()} record${unverified === 1 ? "" : "s"} here have an unverified origin — confirm the source before relying on these numbers.`;

  return (
    <div data-testid={`data-trust-banner-${isSample ? "sample" : "unverified"}`}
      className={`mb-4 rounded-lg border px-4 py-2.5 flex items-start gap-3 text-sm ${tone}`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 leading-snug">
        <span className="font-medium">Data trust:</span> {msg}
        {canManage && (
          <Link to="/admin/data-provenance" data-testid="data-trust-banner-review-link"
            className="inline-flex items-center gap-1 ml-1 underline underline-offset-2 hover:opacity-80">
            <ShieldCheck className="w-3.5 h-3.5" /> Review &amp; verify
          </Link>
        )}
      </div>
      <button data-testid="data-trust-banner-dismiss" onClick={() => setDismissed(true)}
        className="opacity-50 hover:opacity-100" title="Dismiss for this view">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
